import { useState, useEffect, useCallback, useMemo } from "react";
import {
  DESKTOP_HEIGHT_RATIO,
  MIN_PREVIEW_HEIGHT,
  MAX_PREVIEW_HEIGHT,
  MOBILE_PREVIEW_HEIGHT,
  DESKTOP_WIDTH,
  MIN_ASPECT_RATIO,
  MAX_ASPECT_RATIO,
  DEFAULT_CARD_WIDTH,
  DEFAULT_CARD_HEIGHT,
  PORTRAIT_CARD_HEIGHT_PERCENTAGE,
  LANDSCAPE_MIN_PERCENTAGE,
  LANDSCAPE_MAX_PERCENTAGE,
  LANDSCAPE_MIN_WIDTH,
  LANDSCAPE_MAX_WIDTH,
  DESKTOP_TOP_PADDING,
  DESKTOP_BOTTOM_PADDING,
  DESKTOP_SIDE_PADDING,
  MOBILE_TOP_PADDING,
  MOBILE_BOTTOM_PADDING,
  MOBILE_SIDE_PADDING
} from "../cardPreviewConfig";

// Calculate the preview height based on the screen size
// Ensures the height stays within defined minimum and maximum bounds
const getPreviewHeight = (isDesktop = false): number => {
  if (isDesktop) {
    const width = window.innerWidth;
    const vwHeight = Math.round(width * DESKTOP_HEIGHT_RATIO);
    return Math.min(Math.max(vwHeight, MIN_PREVIEW_HEIGHT), MAX_PREVIEW_HEIGHT);
  }
  return MOBILE_PREVIEW_HEIGHT;
};

interface CardDimensions {
  width: number;
  height: number;
}

interface UseCardDimensionsProps {
  containerWidth: number;
  imageUrl?: string;
  imageRef: React.RefObject<HTMLImageElement | null>;
}

/**
 * Hook to calculate and manage card dimensions based on container size, image, and screen type.
 * Provides responsive behavior for desktop and mobile layouts.
 */
export const useCardDimensions = ({
  containerWidth,
  imageUrl,
  imageRef
}: UseCardDimensionsProps) => {
  // Tracks whether the screen is in desktop mode
  const [isDesktop, setIsDesktop] = useState<boolean>(window.innerWidth >= DESKTOP_WIDTH);

  // Represents the height of the card as a percentage
  const [cardHeight, setCardHeight] = useState<string>("85%");

  // Stores the calculated dimensions of the card
  const [cardDimensions, setCardDimensions] = useState<CardDimensions>({ width: 0, height: 0 });

  // Stores the calculated preview height based on the screen size
  const [previewHeight, setPreviewHeight] = useState<number>(getPreviewHeight(isDesktop));

  // Dynamically calculates the height percentage for landscape cards based on container width
  const landscapeCardHeightPercentage = useMemo(() => {
    if (containerWidth <= LANDSCAPE_MIN_WIDTH) return LANDSCAPE_MAX_PERCENTAGE;
    if (containerWidth >= LANDSCAPE_MAX_WIDTH) return LANDSCAPE_MIN_PERCENTAGE;

    const widthRatio =
      (containerWidth - LANDSCAPE_MIN_WIDTH) / (LANDSCAPE_MAX_WIDTH - LANDSCAPE_MIN_WIDTH);
    return (
      LANDSCAPE_MAX_PERCENTAGE - widthRatio * (LANDSCAPE_MAX_PERCENTAGE - LANDSCAPE_MIN_PERCENTAGE)
    );
  }, [containerWidth]);

  // Calculates card dimensions based on the provided dimensions and current layout
  const calculateCardDimensions = useCallback(
    (dimensions: CardDimensions): CardDimensions => {
      const { width, height } = dimensions;
      let aspectRatio = width / height;

      // Clamp aspect ratio within defined bounds
      if (aspectRatio > MAX_ASPECT_RATIO) aspectRatio = MAX_ASPECT_RATIO;
      else if (aspectRatio < MIN_ASPECT_RATIO) aspectRatio = MIN_ASPECT_RATIO;

      // Determine card height based on aspect ratio and layout
      const heightPercentage =
        aspectRatio >= 1
          ? landscapeCardHeightPercentage / 100
          : PORTRAIT_CARD_HEIGHT_PERCENTAGE / 100;
      const cardHeight = previewHeight * heightPercentage;

      // Calculate padding based on screen type
      const topPadding = isDesktop ? DESKTOP_TOP_PADDING : MOBILE_TOP_PADDING;
      const bottomPadding = isDesktop ? DESKTOP_BOTTOM_PADDING : MOBILE_BOTTOM_PADDING;
      const verticalPadding = topPadding + bottomPadding;
      const sidePadding = isDesktop ? DESKTOP_SIDE_PADDING : MOBILE_SIDE_PADDING;
      const horizontalPadding = sidePadding * 2;

      // Calculate content dimensions excluding padding
      const contentHeight = cardHeight - verticalPadding;
      const contentWidth = contentHeight * aspectRatio;

      // Calculate final card width including padding
      const cardWidth = contentWidth + horizontalPadding;

      return {
        width: cardWidth,
        height: cardHeight
      };
    },
    [previewHeight, landscapeCardHeightPercentage, isDesktop]
  );

  // Updates card dimensions when an image is loaded
  const handleImageLoad = useCallback(
    (dimensions: CardDimensions) => {
      const { width, height } = dimensions;
      const aspectRatio = width / height;
      const newHeight =
        aspectRatio >= 1
          ? `${landscapeCardHeightPercentage}%`
          : `${PORTRAIT_CARD_HEIGHT_PERCENTAGE}%`;
      setCardHeight(newHeight);

      const newDimensions = calculateCardDimensions(dimensions);
      setCardDimensions(newDimensions);

      return newDimensions;
    },
    [landscapeCardHeightPercentage, calculateCardDimensions]
  );

  // Handles screen resize events and updates relevant states
  const handleResize = useCallback(() => {
    const newIsDesktop = window.innerWidth >= DESKTOP_WIDTH;
    if (isDesktop !== newIsDesktop) setIsDesktop(newIsDesktop);

    const newHeight = getPreviewHeight(newIsDesktop);
    setPreviewHeight(newHeight);

    if (imageUrl && imageRef.current) {
      const img = imageRef.current;
      const dimensions = {
        width: img.naturalWidth || DEFAULT_CARD_WIDTH,
        height: img.naturalHeight || DEFAULT_CARD_HEIGHT
      };
      const newDimensions = calculateCardDimensions(dimensions);
      setCardDimensions(newDimensions);
    } else {
      // For placeholder, use default dimensions
      const placeholderDimensions = calculateCardDimensions({
        width: DEFAULT_CARD_WIDTH,
        height: DEFAULT_CARD_HEIGHT
      });
      setCardDimensions(placeholderDimensions);
    }
  }, [calculateCardDimensions, imageUrl, imageRef, isDesktop]);

  // Adds a resize event listener and performs initial calculations
  useEffect(() => {
    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleResize]);

  // Updates card dimensions when the image URL changes
  useEffect(() => {
    if (!imageUrl) {
      setCardHeight(`${PORTRAIT_CARD_HEIGHT_PERCENTAGE}%`);

      // Calculate dimensions for placeholder
      const placeholderDimensions = calculateCardDimensions({
        width: DEFAULT_CARD_WIDTH,
        height: DEFAULT_CARD_HEIGHT
      });
      setCardDimensions(placeholderDimensions);
    }
  }, [imageUrl, calculateCardDimensions]);

  return {
    isDesktop,
    cardHeight,
    cardDimensions,
    previewHeight,
    handleImageLoad,
    handleResize
  };
};

export default useCardDimensions;
